import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  #fromCollectionObserver = null;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    this.#initFromCollectionLinks();
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#fromCollectionObserver?.disconnect();
  }

  /**
   * Ajoute ?from_collection= aux liens produit pour le fil d'Ariane sur la fiche produit.
   * Observe les ajouts de contenu (ex. infinite scroll) pour traiter les nouveaux liens.
   */
  #initFromCollectionLinks() {
    const handle = this.getAttribute('data-collection-handle');
    if (!handle) return;

    const addToLinks = () => {
      this.querySelectorAll('a[href*="/products/"]').forEach((a) => {
        try {
          const u = new URL(a.href);
          if (!u.searchParams.has('from_collection')) {
            u.searchParams.set('from_collection', handle);
            a.href = u.toString();
          }
        } catch (_) {}
      });
    };

    addToLinks();
    this.#fromCollectionObserver = new MutationObserver(addToLinks);
    this.#fromCollectionObserver.observe(this, { childList: true, subtree: true });
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
